/**
 * A definitive, type‐level list of QoS (Quality of Service) events.
 *
 * 🔹 **REQUIRED**: Keep this file sorted **alphabetically** for readability and maintainability.
 *
 * • QoS events provide signals for FuseBot Greenlighting during ECS rollouts.
 *   FuseBot runs regression detection on these signals and will redlight
 *   (stop or roll back) a configuration change if a regression is detected.
 * • QoS events are also used by Stoplight to gate release progression
 * • Events added also have to be added to the team lists
 * • These events measure end-to-end user scenarios and are always unsampled
 *
 * For more on our telemetry conventions and data catalog, see:
 *   • https://aka.ms/owa-telemetry
 *   • https://aka.ms/OlkDataCatalog
 */

// Domain-specific QoS Event types (for use in catalog files)
export type AttachmentQoSEvents =
    | 'AttachmentCreation'
    | 'AttachmentDownload'
    | 'AttachmentWellDownloadAll';

export type CalendarQoSEvents =
    | 'changeCalendarViewType'
    | 'SelectCalendar'
    | 'ShowCalendarInlineEvent'
    | 'SwitchCalendarView'
    | 'UpdateDateRange';

export type CalendarTriageAndPlansQoSEvents = 'OpenItemPeek';

export type CollabAndEnhancedExperiencesQoSEvents = 'InlineImageLoad' | 'PreviewAttachmentInSxSE2E';

export type ComposeQoSEvents =
    | 'MailComposeForwardV2'
    | 'MailComposeNewV2'
    | 'MailComposeOfflineSendE2E'
    | 'MailComposeReplyAllV2'
    | 'MailComposeReplyV2'
    | 'MailComposeSend'
    | 'MailComposeSendAQ';

export type OrganizeAndActQoSEvents = 'SelectMailItem' | 'SwitchMailFolder';

export type PeopleAndEcosystemQoSEvents = never;

export type SchedulingQoSEvents =
    | 'CancelEvent'
    | 'DeleteEvent'
    | 'initializeSchedulingAssistantPerf'
    | 'JoinMeetingSDKPerf'
    | 'MeetingOptionsDialogOpen'
    | 'RespondToEvent'
    | 'SaveCalendarEvent'
    | 'SchedulingAssistantAddAttendeePerf'
    | 'showFullComposePerf'
    | 'showReadingPanePerf';

export type SearchAndDiscoveryQoSEvents = 'SearchEndToEnd';

export type TimePlacesQoSEvents = 'LoadCalendarModuleE2E' | 'LoadTimePanelE2E';

export type TnSQoSEvents =
    | 'TnS_CreateFolder'
    | 'TnS_DeleteConversation'
    | 'TnS_DeleteItem'
    | 'TnS_DeleteItemsFromTable'
    | 'TnS_FlagConversation'
    | 'TnS_MailListSelectSort'
    | 'TnS_MarkConversationRead'
    | 'TnS_MarkConversationUnread'
    | 'TnS_MarkItemRead'
    | 'TnS_MarkItemUnread'
    | 'TnS_MoveConversation'
    | 'TnS_MoveItem'
    | 'TnS_SelectFocusedPivot'
    | 'TnS_SelectOtherPivot';

// Combined QoS Events type (all QoS events across domains)
export type QoS_IsCore_Events =
    | AttachmentQoSEvents
    | CalendarQoSEvents
    | CalendarTriageAndPlansQoSEvents
    | CollabAndEnhancedExperiencesQoSEvents
    | ComposeQoSEvents
    | OrganizeAndActQoSEvents
    | PeopleAndEcosystemQoSEvents
    | SchedulingQoSEvents
    | SearchAndDiscoveryQoSEvents
    | TimePlacesQoSEvents
    | TnSQoSEvents;

/**
 * ADD SAMPLED QOS EVENTS HERE
 */
type QoS_Sampled_Events = 'LoadGroupsInLeftNavFromSessionData';

type All_QoS_Events = QoS_IsCore_Events | QoS_Sampled_Events;

/**
 * Adding a QoS event to this list will change it's EventType and add other properties.
 * It will NOT mark it as a "coreEvent", that still needs to be done separately.
 */
export const allQoSEvents: {
    [key in All_QoS_Events]: 1;
} = {
    AttachmentCreation: 1,
    AttachmentDownload: 1,
    AttachmentWellDownloadAll: 1,
    CancelEvent: 1,
    changeCalendarViewType: 1,
    DeleteEvent: 1,
    initializeSchedulingAssistantPerf: 1,
    InlineImageLoad: 1,
    JoinMeetingSDKPerf: 1,
    LoadCalendarModuleE2E: 1,
    LoadGroupsInLeftNavFromSessionData: 1,
    LoadTimePanelE2E: 1,
    MailComposeForwardV2: 1,
    MailComposeNewV2: 1,
    MailComposeOfflineSendE2E: 1,
    MailComposeReplyAllV2: 1,
    MailComposeReplyV2: 1,
    MailComposeSend: 1,
    MailComposeSendAQ: 1,
    MeetingOptionsDialogOpen: 1,
    OpenItemPeek: 1,
    PreviewAttachmentInSxSE2E: 1,
    RespondToEvent: 1,
    SaveCalendarEvent: 1,
    SchedulingAssistantAddAttendeePerf: 1,
    SearchEndToEnd: 1,
    SelectCalendar: 1,
    SelectMailItem: 1,
    ShowCalendarInlineEvent: 1,
    showFullComposePerf: 1,
    showReadingPanePerf: 1,
    SwitchCalendarView: 1,
    SwitchMailFolder: 1,
    TnS_CreateFolder: 1,
    TnS_DeleteConversation: 1,
    TnS_DeleteItem: 1,
    TnS_DeleteItemsFromTable: 1,
    TnS_FlagConversation: 1,
    TnS_MailListSelectSort: 1,
    TnS_MarkConversationRead: 1,
    TnS_MarkConversationUnread: 1,
    TnS_MarkItemRead: 1,
    TnS_MarkItemUnread: 1,
    TnS_MoveConversation: 1,
    TnS_MoveItem: 1,
    TnS_SelectFocusedPivot: 1,
    TnS_SelectOtherPivot: 1,
    UpdateDateRange: 1,
};

export const qosEvents: string[] = Object.keys(allQoSEvents);
